import React from 'react';
import makeStyles from '@mui/styles/makeStyles';
import { formatSnakeToCamelCaseLabel } from 'utils/formatters';
import { Box, Stack, Typography, Tooltip } from '@mui/material';
import { Cancel, CheckCircle, Circle, InfoOutlined } from '@mui/icons-material';
import { green, lightGreen, red, common, blue } from '@mui/material/colors';
import { RenderWithCondition } from 'components/shared/Conditional/RenderWithCondition';
import { FailedRule, FactToCheck, ProductConfig } from '../../../../../types';
import { determineIsProductConfigIncludeAllFields } from '../../../../../lib';

export interface Props {
  factsToCheck: FactToCheck[];
  failedRules: FailedRule[];
  productConfiguration: ProductConfig;
}

export const ProductConfigurationResults = ({ factsToCheck, productConfiguration, failedRules }: Props) => {
  const classes = useStyles();

  const { isConfigComplete, requiresForSet } = determineIsProductConfigIncludeAllFields(
    factsToCheck,
    productConfiguration
  );

  if (!isConfigComplete) {
    return (
      <Box className={classes.container} sx={{ background: blue[600] }}>
        <Box className="resultWrapper">
          <Tooltip
            title={
              <Stack>
                <Typography variant="body2">To get the price of the product, fill in the fields:</Typography>
                {requiresForSet.map(fact => (
                  <Typography variant="body2" key={fact}>
                    - {fact}
                  </Typography>
                ))}
              </Stack>
            }
          >
            <InfoOutlined sx={{ color: common.white }} />
          </Tooltip>

          <Typography component="span" variant="subtitle2" color={common.white}>
            Requires Configuration
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <RenderWithCondition
      condition={!!productConfiguration.isPricingValid}
      fallBack={
        <Box className={classes.container} sx={{ background: red[100] }}>
          <Box className="resultWrapper">
            <Cancel sx={{ color: red[600] }} />

            <Typography component="span" variant="subtitle2" color={red[600]}>
              Ineligible
            </Typography>
          </Box>

          {!!failedRules.length && (
            <Box className="reasonsList">
              {failedRules.map(({ fact }) => (
                <Stack direction="row" alignItems="center" gap="0.5rem">
                  <Circle sx={{ fontSize: '0.3rem' }} />
                  <Typography key={fact} variant="body2">
                    {`${formatSnakeToCamelCaseLabel(fact)} is missing or does not meet the product’s criteria`}
                  </Typography>
                </Stack>
              ))}
            </Box>
          )}
        </Box>
      }
    >
      <Box className={classes.container} sx={{ background: lightGreen[100] }}>
        <Box className="resultWrapper">
          <CheckCircle sx={{ color: green[600] }} />

          <Typography component="span" variant="subtitle2" color={green[600]}>
            Qualifies
          </Typography>
        </Box>
      </Box>
    </RenderWithCondition>
  );
};

const useStyles = makeStyles({
  container: {
    gap: '0.5rem',
    display: 'flex',
    borderRadius: '4px',
    padding: '0.5rem 3rem',
    flexDirection: 'column',

    '& .resultWrapper': {
      gap: '0.5rem',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },

    '& .reasonsList': {
      display: 'flex',
      flexDirection: 'column',
    },
  },
});
